import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Alert,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Cookie as CookieIcon,
  Update as UpdateIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Storage as StorageIcon,
  Shield as ShieldIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';

export const PrivacyPolicy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState('introduction');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const lastUpdated = "February 16, 2026";
  const effectiveDate = "February 16, 2026";

  const privacyData = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: <InfoIcon />,
      content: [
        {
          subtitle: 'Our Commitment to Privacy',
          text: 'At GlobalCommand Center (operated by Oruclicks), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our social media management platform.'
        },
        {
          subtitle: 'Scope of This Policy',
          text: 'This policy applies to all users of our platform, including visitors, registered users, and subscribers. It covers all information collected through our website, mobile applications, and related services.'
        },
        {
          subtitle: 'Your Consent',
          text: 'By using our services, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our services.'
        }
      ]
    },
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: <StorageIcon />,
      content: [
        {
          subtitle: 'Personal Information',
          text: 'We collect information that you provide directly to us, including: name, email address, phone number, billing information, company name, job title, profile information, and any other information you choose to provide.'
        },
        {
          subtitle: 'Social Media Account Data',
          text: 'When you connect your social media accounts (Facebook, Instagram, Twitter, LinkedIn, etc.), we collect and store access tokens and certain profile information necessary to provide our services. We only access data that you explicitly authorize through OAuth connections.'
        },
        {
          subtitle: 'Usage Information',
          text: 'We automatically collect information about how you interact with our platform, including: IP address, browser type, device information, operating system, pages viewed, time spent on pages, access times, referring URLs, and clickstream data.'
        },
        {
          subtitle: 'Cookies and Tracking Technologies',
          text: 'We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities and preferences. This helps us improve user experience, analyze trends, and personalize content.'
        },
        {
          subtitle: 'Analytics Data',
          text: 'We collect data about your social media performance, including post engagement, audience demographics, follower growth, and other metrics necessary to provide analytics and reporting features.'
        }
      ]
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      icon: <DescriptionIcon />,
      content: [
        {
          subtitle: 'Service Delivery',
          text: 'We use your information to provide, maintain, and improve our services, including: managing your social media accounts, scheduling and publishing posts, providing analytics and insights, and offering customer support.'
        },
        {
          subtitle: 'Communication',
          text: 'We may use your email address and phone number to send you: service-related notifications, account updates, security alerts, customer support responses, promotional materials (with your consent), and newsletters about new features and updates.'
        },
        {
          subtitle: 'Platform Improvement',
          text: 'We analyze usage patterns and feedback to improve our platform, develop new features, optimize performance, and enhance user experience.'
        },
        {
          subtitle: 'Security and Fraud Prevention',
          text: 'We use information to protect our platform and users from unauthorized access, fraudulent activities, security breaches, and to comply with legal obligations.'
        },
        {
          subtitle: 'Personalization',
          text: 'We use your preferences and usage history to personalize your experience, recommend relevant features, and provide customized content suggestions.'
        }
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing & Disclosure',
      icon: <PeopleIcon />,
      content: [
        {
          subtitle: 'Social Media Platforms',
          text: 'We share your content with social media platforms (Facebook, Instagram, Twitter, LinkedIn, etc.) as necessary to publish posts and perform actions you authorize through our platform.'
        },
        {
          subtitle: 'Service Providers',
          text: 'We may share your information with third-party service providers who perform services on our behalf, including: cloud hosting providers, payment processors, email delivery services, analytics providers, and customer support tools. These providers are bound by confidentiality agreements.'
        },
        {
          subtitle: 'Business Transfers',
          text: 'In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you via email or prominent notice on our platform before your information becomes subject to a different privacy policy.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information if required by law, court order, or government request, or if we believe disclosure is necessary to: comply with legal obligations, protect our rights and property, prevent fraud or abuse, or protect the safety of our users and the public.'
        },
        {
          subtitle: 'With Your Consent',
          text: 'We may share your information with third parties when you explicitly consent to such sharing, such as when integrating with additional tools or services.'
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: <ShieldIcon />,
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement industry-standard security measures to protect your information, including: encryption of data in transit and at rest (AES-256), secure socket layer (SSL/TLS) technology, regular security audits and penetration testing, access controls and authentication mechanisms, and employee security training.'
        },
        {
          subtitle: 'Data Centers',
          text: 'Our data is stored in secure, SOC 2 compliant data centers with physical security controls, redundant systems, and regular backups.'
        },
        {
          subtitle: 'Access Controls',
          text: 'We limit access to your personal information to authorized employees and contractors who need access to perform their job functions. All personnel with access are subject to confidentiality obligations.'
        },
        {
          subtitle: 'No Absolute Security',
          text: 'While we strive to protect your information, no method of electronic storage or internet transmission is 100% secure. We cannot guarantee absolute security but continuously work to enhance our security measures.'
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: <LockIcon />,
      content: [
        {
          subtitle: 'Retention Period',
          text: 'We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Active account data is retained while your account remains active.'
        },
        {
          subtitle: 'Account Deletion',
          text: 'When you delete your account, we will delete or anonymize your personal information within 90 days, except where we are required to retain certain information for legal, tax, or regulatory purposes.'
        },
        {
          subtitle: 'Backup Data',
          text: 'Deleted data may persist in backup systems for up to 90 days before being permanently removed. This ensures we can recover data in case of system failures.'
        }
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Privacy Rights',
      icon: <GavelIcon />,
      content: [
        {
          subtitle: 'Access and Portability',
          text: 'You have the right to access your personal information and request a copy of your data in a portable format (CSV or JSON). You can export your data through your account settings or by contacting us.'
        },
        {
          subtitle: 'Correction and Updates',
          text: 'You can update your account information at any time through your account settings. You may also contact us to correct any inaccurate information.'
        },
        {
          subtitle: 'Deletion Rights',
          text: 'You have the right to request deletion of your personal information. You can delete your account through account settings or contact us for assistance. Some information may be retained as required by law.'
        },
        {
          subtitle: 'Opt-Out Rights',
          text: 'You can opt out of marketing communications by clicking the unsubscribe link in emails or updating your notification preferences in account settings. You can also opt out of certain cookies through your browser settings.'
        },
        {
          subtitle: 'Do Not Track',
          text: 'Some browsers include a "Do Not Track" feature. Our platform does not currently respond to Do Not Track signals, as there is no industry standard for how to respond to such signals.'
        },
        {
          subtitle: 'GDPR Rights (EU Users)',
          text: 'If you are in the European Union, you have additional rights under GDPR, including: the right to object to processing, the right to restrict processing, the right to withdraw consent, and the right to lodge a complaint with a supervisory authority.'
        },
        {
          subtitle: 'CCPA Rights (California Users)',
          text: 'If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), including: the right to know what information we collect, the right to delete your information, the right to opt-out of sale (we do not sell personal information), and the right to non-discrimination.'
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies & Tracking',
      icon: <CookieIcon />,
      content: [
        {
          subtitle: 'Types of Cookies We Use',
          text: 'Essential Cookies: Necessary for the platform to function properly. Performance Cookies: Help us understand how users interact with our platform. Functional Cookies: Remember your preferences and settings. Marketing Cookies: Track your activity across websites to deliver targeted advertising.'
        },
        {
          subtitle: 'Third-Party Cookies',
          text: 'We use third-party services like Google Analytics, Mixpanel, and other analytics providers that may set cookies to collect usage data and improve our services.'
        },
        {
          subtitle: 'Managing Cookies',
          text: 'You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of our platform. You can also use our cookie consent manager to customize your preferences.'
        }
      ]
    },
    {
      id: 'children',
      title: "Children's Privacy",
      icon: <SecurityIcon />,
      content: [
        {
          subtitle: 'Age Restrictions',
          text: 'Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.'
        },
        {
          subtitle: 'Parental Consent',
          text: 'If we learn that we have collected information from a child under 18 without parental consent, we will delete that information immediately. If you believe a child has provided us with personal information, please contact us.'
        }
      ]
    },
    {
      id: 'international',
      title: 'International Data Transfers',
      icon: <LocationIcon />,
      content: [
        {
          subtitle: 'Cross-Border Transfers',
          text: 'Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws.'
        },
        {
          subtitle: 'Data Protection Safeguards',
          text: 'We use appropriate safeguards for international transfers, including: Standard Contractual Clauses approved by the European Commission, Privacy Shield frameworks (where applicable), and other legally approved mechanisms.'
        }
      ]
    },
    {
      id: 'changes',
      title: 'Changes to This Policy',
      icon: <UpdateIcon />,
      content: [
        {
          subtitle: 'Policy Updates',
          text: 'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or for other operational reasons.'
        },
        {
          subtitle: 'Notification of Changes',
          text: 'We will notify you of material changes by: posting the updated policy on our website with a new "Last Updated" date, sending an email notification to the address associated with your account, and displaying a prominent notice on our platform.'
        },
        {
          subtitle: 'Your Continued Use',
          text: 'Your continued use of our services after any changes to this Privacy Policy constitutes your acceptance of the updated policy.'
        }
      ]
    }
  ];

  const cookieTypes = [
    {
      type: 'Essential Cookies',
      purpose: 'Required for platform functionality',
      examples: 'Authentication, security, session management',
      canDisable: 'No'
    },
    {
      type: 'Performance Cookies',
      purpose: 'Analyze platform performance and usage',
      examples: 'Google Analytics, error tracking',
      canDisable: 'Yes'
    },
    {
      type: 'Functional Cookies',
      purpose: 'Remember preferences and settings',
      examples: 'Language preference, theme selection',
      canDisable: 'Yes'
    },
    {
      type: 'Marketing Cookies',
      purpose: 'Deliver personalized advertisements',
      examples: 'Retargeting pixels, ad networks',
      canDisable: 'Yes'
    }
  ];

  const contactInfo = {
    company: 'Oruclicks',
    email: 'info@oruclick.com',
    // address: 'Oruclicks Technologies Pvt Ltd, Chennai, Tamil Nadu, India'
  };

  return (
    <Box sx={{ py: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3, 
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ fontSize: 32, mr: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Privacy Policy
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3, maxWidth: '80%' }}>
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <UpdateIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {lastUpdated}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Effective Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {effectiveDate}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Version
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      2.1
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Print Terms"
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Download PDF"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Share"
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Background Pattern */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: '50%', 
              height: '100%', 
              opacity: 0.1,
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="27" cy="7" r="2"/%3E%3Ccircle cx="47" cy="7" r="2"/%3E%3Ccircle cx="7" cy="27" r="2"/%3E%3Ccircle cx="27" cy="27" r="2"/%3E%3Ccircle cx="47" cy="27" r="2"/%3E%3Ccircle cx="7" cy="47" r="2"/%3E%3Ccircle cx="27" cy="47" r="2"/%3E%3Ccircle cx="47" cy="47" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }} 
          />
        </Paper>

        {/* Important Notice */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 24 }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Important Notice
          </Typography>
          <Typography variant="body2">
            This Privacy Policy explains how GlobalCommand Center collects, uses, and protects your personal information. 
            By using our services, you agree to the collection and use of information in accordance with this policy.
          </Typography>
        </Alert>

        {/* Quick Navigation */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Navigation
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {privacyData.map((section) => (
              <Chip
                key={section.id}
                label={section.title}
                onClick={() => setExpanded(section.id)}
                variant={expanded === section.id ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: expanded === section.id ? '#4B5AB6' : 'transparent',
                  color: expanded === section.id ? 'white' : '#4B5AB6',
                  '&:hover': {
                    bgcolor: expanded === section.id ? '#3a4891' : '#f1f5f9'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Privacy Content */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {privacyData.map((section, index) => (
            <Accordion
              key={section.id}
              expanded={expanded === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
                borderBottom: index < privacyData.length - 1 ? '1px solid #e2e8f0' : 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: expanded === section.id ? '#f8fafc' : 'white',
                  '&:hover': { bgcolor: '#f8fafc' },
                  py: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4B5AB6' }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ px: 4, pb: 4 }}>
                {section.content.map((item, itemIndex) => (
                  <Box key={itemIndex} sx={{ mb: itemIndex < section.content.length - 1 ? 4 : 0 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          bgcolor: '#4B5AB6' 
                        }} 
                      />
                      {item.subtitle}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7, 
                        color: '#475569',
                        pl: 2
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        {/* Cookie Information Table */}
        <Card sx={{ mt: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CookieIcon sx={{ color: '#4B5AB6', mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Cookie Types We Use
              </Typography>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Cookie Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Can Disable?</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cookieTypes.map((cookie, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 500 }}>{cookie.type}</TableCell>
                      <TableCell>{cookie.purpose}</TableCell>
                      <TableCell>{cookie.examples}</TableCell>
                      <TableCell>
                        <Chip 
                          label={cookie.canDisable} 
                          size="small"
                          sx={{
                            bgcolor: cookie.canDisable === 'Yes' ? '#dcfce7' : '#fee2e2',
                            color: cookie.canDisable === 'Yes' ? '#166534' : '#991b1b',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Your Rights Summary */}
        <Card sx={{ mt: 4, borderRadius: 2, bgcolor: '#f0f9ff' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ShieldIcon sx={{ color: '#4B5AB6', mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Your Privacy Rights Summary
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Request a copy of your personal data we hold
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Correction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update or correct inaccurate information
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Deletion
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Request deletion of your personal data
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Portability
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive your data in a portable format
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Opt-Out
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unsubscribe from marketing communications
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckIcon sx={{ color: '#4B5AB6', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Right to Object
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Object to certain types of data processing
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card sx={{ mt: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
              Contact Us About Privacy
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: '#475569' }}>
              If you have any questions about this Privacy Policy or want to exercise your privacy rights, please contact us:
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EmailIcon sx={{ color: '#4B5AB6' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email"
                      secondary={
                        <Link 
                          href={`mailto:${contactInfo.email}`}
                          sx={{ color: '#4B5AB6', textDecoration: 'none' }}
                        >
                          {contactInfo.email}
                        </Link>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationIcon sx={{ color: '#4B5AB6' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Address"
                      secondary={contactInfo.address}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            
            <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Privacy Response Time:</strong> We typically respond to privacy requests within 30 days.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 4, textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            © 2026 GlobalCommand Center (Oruclicks). All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This privacy policy is effective as of {effectiveDate}. Last updated: {lastUpdated}.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;